/**
 * The owner's equity statement: a business taxed with its owner (a
 * single-member LLC or sole proprietorship, 0094; PLAN_tax_treatments §2.2),
 * one fiscal year at a time.
 *
 * THE FIGURES ARE THE SERVER'S. GET /reports/owners-equity reads the start and
 * the end from the balance sheet and the year's net income from the income
 * statement (apps/api/src/services/owners-equity.ts), so this screen does no
 * arithmetic of its own.
 *
 * Its own address beside Cashflow, as the shareholder report is an S
 * corporation's: the three statements share a From/To period, and this one has
 * a fiscal year instead. A prompt for the CPA, never a ruling.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type OwnersEquityStatement } from '../api.js';
import { formatCents } from '../lib/money.js';

export function OwnersEquityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [statement, setStatement] = useState<OwnersEquityStatement | null>(null);
  // The current fiscal year, from the first answer, so the choices end at it.
  const [current, setCurrent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getOwnersEquity(year)
      .then((result) => {
        if (!live) return;
        setStatement(result.statement);
        if (year === undefined) setCurrent(result.statement.fiscalYear);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : "Could not read the owner's equity statement.");
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  // The year in the address, even while it loads, so the choice on screen is never one year while the page reads another.
  const shown = year ?? statement?.fiscalYear;
  const latest = current ?? (shown !== undefined ? Math.max(shown, new Date().getFullYear()) : undefined);
  const choices = latest === undefined ? [] : Array.from({ length: 6 }, (_, back) => latest - back);
  if (shown !== undefined && !choices.includes(shown)) choices.push(shown);

  return (
    <section className="reports owners-equity">
      <PrintHeading covers={shown === undefined ? undefined : `Fiscal year ${shown}`} />
      <header className="row reports-head">
        <h1>Owner&apos;s Equity</h1>
        <span className="muted">What the owner put in, what the business earned, and what the owner took out, for one fiscal year.</span>
        <PrintButton />
      </header>

      <nav className="row tabs" aria-label="Report">
        <Link to="/reports?report=income" className="tab">
          Income Statement
        </Link>
        <Link to="/reports?report=balance" className="tab">
          Balance Sheet
        </Link>
        <Link to="/reports?report=trial" className="tab">
          Trial Balance
        </Link>
        <Link to="/reports/cashflow" className="tab">
          Cashflow
        </Link>
        <span className="tab is-current" aria-current="page">
          Owner&apos;s Equity
        </span>
      </nav>

      {shown !== undefined && (
        <div className="row report-period">
          <label>
            Fiscal Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (statement === null || statement.fiscalYear !== shown) && <p className="muted">Loading…</p>}
      {error === null && statement !== null && statement.fiscalYear === shown && <Statement statement={statement} />}
    </section>
  );
}

function Statement({ statement }: { statement: OwnersEquityStatement }) {
  const rows: Array<[string, number]> = [
    [`Owner's Equity, ${statement.from}`, statement.startCents],
    ["Owner's Contributions", statement.contributionsCents],
    ['Net Income', statement.netIncomeCents],
    ["Owner's Draws", -statement.drawsCents],
  ];
  if (statement.otherChangesCents !== 0) rows.push(['Other Changes', statement.otherChangesCents]);
  return (
    <div className="report">
      <h2>Statement of Owner&apos;s Equity</h2>
      <p className="muted">
        {statement.from} to {statement.to}
        {statement.yearComplete ? '' : ', the year so far'}
      </p>
      <div className="muted">
        <InfoNote label="Where These Come From">
          <p>
            The start and the end are the balance sheet&apos;s total equity on the day before the year begins and on{' '}
            {statement.yearComplete ? "the year's last day" : 'today'}. Net income is the income statement&apos;s for the
            same days; contributions and draws are what was posted to Owner&apos;s Contributions and Owner&apos;s Draws.
          </p>
          <p>
            The year-end close moves the year&apos;s net income into Owner&apos;s Equity and changes nothing here. Draws
            are not closed: they stay on their account until your CPA says otherwise.
          </p>
        </InfoNote>
      </div>
      <table className="lines">
        <tbody>
          {rows.map(([label, cents]) => (
            <tr key={label}>
              <td>{label}</td>
              <td className="num">{formatCents(cents)}</td>
            </tr>
          ))}
          <tr className="report-total">
            <th scope="row">Owner&apos;s Equity, {statement.to}</th>
            <td className="num">{formatCents(statement.endCents)}</td>
          </tr>
        </tbody>
      </table>
      {statement.otherChangesCents !== 0 && (
        <p className="muted">
          Other changes are entries straight to an equity account that are neither a contribution, a draw nor income, an
          opening balance for one. Your CPA decides how the return shows them.
        </p>
      )}
    </div>
  );
}
